import { z } from "zod";
import {
	systemSettingSchema,
	systemSettingsMapSchema,
} from "./systemSettings.schemas";

/**
 * =============================================================================
 * COMMON SCHEMAS
 * =============================================================================
 */

/**
 * Standard API success response base structure
 */
export const ApiSuccessResponseBaseSchema = z.object({
	message: z.string(),
	success: z.literal(true),
});

/**
 * Standard API error response base structure
 */
export const ApiErrorResponseBaseSchema = z.object({
	message: z.string(),
	success: z.literal(false),
});

/**
 * =============================================================================
 * SYSTEM SETTINGS API SCHEMAS
 * =============================================================================
 */

/**
 * GET /system-settings - Success Response (200)
 */
export const GetSystemSettings200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: systemSettingsMapSchema,
	}).strict();

/**
 * GET /system-settings - Server Error Response (500)
 */
export const GetSystemSettings500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /system-settings/:name - Path Parameters
 */
export const GetSystemSettingPathParamsSchema = z.object({
	name: z.string().min(1).max(50),
});

/**
 * GET /system-settings/:name - Success Response (200)
 */
export const GetSystemSetting200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: systemSettingSchema.nullable(),
	}).strict();

/**
 * GET /system-settings/:name - Not Found Response (404)
 */
export const GetSystemSetting404ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /system-settings/:name - Server Error Response (500)
 */
export const GetSystemSetting500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /system-settings/public - Success Response (200)
 */
export const GetPublicSettings200ResponseSchema =
	ApiSuccessResponseBaseSchema.extend({
		data: systemSettingsMapSchema,
	}).strict();

/**
 * GET /system-settings/public - Server Error Response (500)
 */
export const GetPublicSettings500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * PUT /system-settings/:name - Path Parameters
 */
export const UpdateSystemSettingPathParamsSchema = z.object({
	name: z.string().min(1).max(50),
});

/**
 * PUT /system-settings/:name - Request Body
 */
export const UpdateSystemSettingRequestSchema = z
	.object({
		name: z.string().min(1).max(50),
		label: z.string().min(1).max(50),
		valueFlag: z.boolean().nullable().optional(),
		valueString: z.string().max(500).nullable().optional(),
		valueNumber: z.number().nullable().optional(),
		isPublic: z.boolean().optional(),
	})
	.strict();

/**
 * PUT /system-settings/:name - Success Response (200)
 */
export const UpdateSystemSetting200ResponseSchema =
	ApiSuccessResponseBaseSchema;

/**
 * PUT /system-settings/:name - Bad Request Response (400)
 */
export const UpdateSystemSetting400ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * PUT /system-settings/:name - Server Error Response (500)
 */
export const UpdateSystemSetting500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * PUT /system-settings/bulk - Request Body
 */
export const UpdateBulkSystemSettingsRequestSchema = z
	.object({
		settings: z.array(
			z.object({
				name: z.string().min(1).max(50),
				label: z.string().min(1).max(50),
				valueFlag: z.boolean().nullable().optional(),
				valueString: z.string().max(500).nullable().optional(),
				valueNumber: z.number().nullable().optional(),
				isPublic: z.boolean().optional(),
			}),
		),
	})
	.strict();

/**
 * PUT /system-settings/bulk - Success Response (200)
 */
export const UpdateBulkSystemSettings200ResponseSchema =
	ApiSuccessResponseBaseSchema;

/**
 * PUT /system-settings/bulk - Bad Request Response (400)
 */
export const UpdateBulkSystemSettings400ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * PUT /system-settings/bulk - Server Error Response (500)
 */
export const UpdateBulkSystemSettings500ResponseSchema =
	ApiErrorResponseBaseSchema;

/**
 * DELETE /system-settings/:name - Path Parameters
 */
export const DeleteSystemSettingPathParamsSchema = z.object({
	name: z.string().min(1).max(50),
});

/**
 * DELETE /system-settings/:name - Success Response (200)
 */
export const DeleteSystemSetting200ResponseSchema =
	ApiSuccessResponseBaseSchema;

/**
 * DELETE /system-settings/:name - Bad Request Response (400)
 */
export const DeleteSystemSetting400ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * DELETE /system-settings/:name - Not Found Response (404)
 */
export const DeleteSystemSetting404ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * DELETE /system-settings/:name - Server Error Response (500)
 */
export const DeleteSystemSetting500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * =============================================================================
 * BRANDING FILE UPLOAD API SCHEMAS
 * =============================================================================
 */

/**
 * GET /system-settings/branding/favicon - Success Response (200)
 */
export const GetFavicon200ResponseSchema = ApiSuccessResponseBaseSchema.extend({
	data: z.string().nullable(),
}).strict();

/**
 * GET /system-settings/branding/favicon - Server Error Response (500)
 */
export const GetFavicon500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * POST /system-settings/branding/favicon - Success Response (200)
 */
export const UploadFavicon200ResponseSchema = ApiSuccessResponseBaseSchema;

/**
 * POST /system-settings/branding/favicon - Bad Request Response (400)
 */
export const UploadFavicon400ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * POST /system-settings/branding/favicon - Server Error Response (500)
 */
export const UploadFavicon500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /system-settings/branding/logo - Success Response (200)
 */
export const GetLogo200ResponseSchema = ApiSuccessResponseBaseSchema.extend({
	data: z.string().nullable(),
}).strict();

/**
 * GET /system-settings/branding/logo - Server Error Response (500)
 */
export const GetLogo500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * POST /system-settings/branding/logo - Success Response (200)
 */
export const UploadLogo200ResponseSchema = ApiSuccessResponseBaseSchema;

/**
 * POST /system-settings/branding/logo - Bad Request Response (400)
 */
export const UploadLogo400ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * POST /system-settings/branding/logo - Server Error Response (500)
 */
export const UploadLogo500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * GET /system-settings/branding/store-pic - Success Response (200)
 */
export const GetStorePic200ResponseSchema = ApiSuccessResponseBaseSchema.extend(
	{
		data: z.string().nullable(),
	},
).strict();

/**
 * GET /system-settings/branding/store-pic - Server Error Response (500)
 */
export const GetStorePic500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * POST /system-settings/branding/store-pic - Success Response (200)
 */
export const UploadStorePic200ResponseSchema = ApiSuccessResponseBaseSchema;

/**
 * POST /system-settings/branding/store-pic - Bad Request Response (400)
 */
export const UploadStorePic400ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * POST /system-settings/branding/store-pic - Server Error Response (500)
 */
export const UploadStorePic500ResponseSchema = ApiErrorResponseBaseSchema;

/**
 * =============================================================================
 * BACKWARD COMPATIBILITY ALIASES (DEPRECATED)
 * =============================================================================
 */

// @deprecated Use GetSystemSettings200ResponseSchema instead
export const systemSettingsResponseSchema = GetSystemSettings200ResponseSchema;

// @deprecated Use GetSystemSetting200ResponseSchema instead
export const systemSettingResponseSchema = GetSystemSetting200ResponseSchema;

// @deprecated Use ApiSuccessResponseBaseSchema or ApiErrorResponseBaseSchema instead
export const commonResponseSchema = z.object({
	message: z.string(),
	success: z.boolean(),
});

// @deprecated Use GetFavicon200ResponseSchema, GetLogo200ResponseSchema, or GetStorePic200ResponseSchema instead
export const fileUploadResponseSchema = z.object({
	data: z.string().nullable(),
	message: z.string(),
	success: z.boolean(),
});

// @deprecated Use UpdateSystemSettingRequestSchema instead
export const updateSystemSettingRequestSchema =
	UpdateSystemSettingRequestSchema;

// @deprecated Use UpdateBulkSystemSettingsRequestSchema instead
export const bulkUpdateSystemSettingsRequestSchema =
	UpdateBulkSystemSettingsRequestSchema;

// @deprecated Use GetSystemSettingPathParamsSchema instead
export const systemSettingNameParamSchema = GetSystemSettingPathParamsSchema;
